import * as React from "react";

type AutoCollection = null | "on" | "off";

type BillingAddress = {
  city: string;
  line1: string;
  line2?: string;
  line3?: string;
  state: string;
  zip: string;
};

export type BillingInfoCardPropTypes = {
  billingAddress?: BillingAddress;
  paymentMethodOnFile: boolean;
  fetchData: (card: ChargebeeInfoCardTypes) => void;
  loading: boolean;
  openPortal: OpenPortal;
};

export enum BillingType {
  Fixed = "fixed_prepay",
  Flex = "flex_prepay",
  None = "none",
  PayAsYouGo = "pay_as_you_go",
}

export type CancelModalPropTypes = {
  closeModal: () => void;
  modal: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type SwitchPlanModalProps = {
  closeModal: () => void;
  companyId: string;
  fetchData: () => void;
  modal: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type CancelTrialCardPropTypes = {
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type Card = {
  expiration: string;
  last4: string;
  type: CreditCardTypes;
};

export type CatalogOverrideModalBodyPropTypes = {
  catalogStatus: string;
  closeModal: () => void;
  fetchData: () => void;
  overrideDate: string;
  catalogShutoff: boolean;
};

export type ChargebeeInfoCardTypes = "billing" | "contact" | "";

export type ContactInfoCardPropTypes = {
  companyName?: string;
  email?: string;
  fetchData: (card: ChargebeeInfoCardTypes) => void;
  loading: boolean;
  openPortal: OpenPortal;
  phone?: string;
};

export type ContainerData = {
  billingType?: BillingType;
  chargebeeSite?: string;
  chargebeeInfo?: {
    billingAddress: BillingAddress;
    card?: Card;
    companyName?: string;
    email?: string;
    phone?: string;
  };
  companyBlocklisted?: boolean;
  companyId?: string;
  showSuperAdminFeature?: boolean;
  subscriptionAutoCollection?: AutoCollection;
  subscriptionId?: string;
  subscriptionTrialDisplay?: string;
  subscriptionStatus?: string;
  subscriptionType?: string;
  paymentDueOn?: string;
  paymentMethodOnFile?: boolean;
  refillAmount?: string;
  refillThreshold?: string;
  trialEndDate?: string;
  trialing?: boolean;
  userCount?: number;
};

// NOTE: credit card types are from Chargebee: https://apidocs.chargebee.com/docs/api/cards?prod_cat_ver=2#card_card_type
export type CreditCardTypes =
  | "american_express"
  | "bancontact"
  | "diners_club"
  | "discover"
  | "jcb"
  | "mastercard"
  | "not_applicable"
  | "other"
  | "visa"
  | null;

export type CreditCardPaymentMethodPropTypes = {
  cardType: CreditCardTypes;
  expiration?: string;
  last4?: string;
  openPortal: OpenPortal;
  rewardBillingType: BillingType;
  setRefetchData: (refetchData: boolean) => void;
  subscriptionAutoCollection: AutoCollection;
};

export type CSVExportModalBodyProps = {
  closeModal: () => void;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type DeactivatedBannerPropTypes = {
  cardType?: CreditCardTypes;
  companyBlocklisted: boolean;
  companyId: string;
  hasPaymentMethod: boolean;
  last4?: string;
  openPortal: OpenPortal;
  subscriptionType: string;
  setRefetchData: (refetchData: boolean) => void;
  updateToastMessage: (ToastMessageOptionTypes) => void;
  userCount: number;
  modal: string;
  setModal: (modal: string) => void;
};

export type EditChargebeeInfoIconPropTypes = {
  altText: string;
  amplitudeEventName: string;
  closeModalCallback?: () => void;
  openPortal: OpenPortal;
  portalSection: PortalSection;
  testId: string;
};

export type EditPONumbersIconPropTypes = {
  openModal: () => void;
  type: "add" | "edit";
};

export type FeesModalPropTypes = {
  closeModal: () => void;
  modal: string;
};

export type FlexConfirmModalProps = {
  card: Card;
  closeModal: () => void;
  modal: string;
  setModal: (modal: string) => void;
};

export type FlexSettingsModalProps = {
  amount: string;
  closeModal: () => void;
  existingAmount?: string;
  isModifying: boolean;
  modal: string;
  setAmount: (amount: string) => void;
  setModal: (modal: string) => void;
  userCount: number;
};

export type FlexPaymentModalProps = {
  amount: string;
  closeModal: () => void;
  companyId: string;
  fetchData: () => void;
  isModifying: boolean;
  modal: string;
  rewardBalance: string;
  setLoading: (loading: boolean) => void;
  setSuccessMessage: (message: string) => void;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type InvoiceModalPropTypes = {
  closeModal: () => void;
  modal: string;
  paymentDueOn?: string;
  refillAmount?: string;
  refillThreshold?: string;
  type: "both" | "reward" | "subscription";
};

export type InvoiceDownloadIconPropTypes = {
  invoiceId: string;
  testId: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type MakePaymentButtonPropTypes = {
  prevPaymentError?: string;
  testId: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type InvoicePaymentMethodPropTypes = {
  cardType: CreditCardTypes;
  last4: string;
  fetchData?: () => void;
  openPortal: OpenPortal;
  paymentDueOn?: string;
  paymentMethodOnFile: boolean;
  refillAmount?: string;
  refillThreshold?: string;
  setPageLoading: (loading: boolean) => void;
  subscriptionId?: string;
  type: "both" | "reward" | "subscription";
  updateToastMessage?: (ToastMessageOptionTypes) => void;
};

export type ManualInvoiceModalBodyProps = {
  closeModal: () => void;
  companyId: string;
  fetchData: () => void;
  netTermDays: number;
  prepayAmount: number;
  prepayAmountDisplay: string;
  prepayThresholdDisplay: string;
  rewardBalanceDisplay: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type OpenPortal = (
  event: React.ChangeEvent,
  sectionType: PortalSection,
  handleClose: () => void
) => void;

export type PaymentMethodPropTypes = {
  cardType: CreditCardTypes;
  expiration?: string;
  last4?: string;
  loading?: boolean;
  fetchData?: () => void;
  openPortal: OpenPortal;
  paymentDueOn?: string;
  paymentMethodOnFile?: boolean;
  refillAmount?: string;
  refillThreshold?: string;
  rewardBillingType: BillingType;
  setPageLoading?: (loading: boolean) => void;
  setRefetchData: (refetchData: boolean) => void;
  subscriptionAutoCollection: AutoCollection;
  subscriptionId?: string;
  type: "both" | "reward" | "subscription";
  updateToastMessage?: (ToastMessageOptionTypes) => void;
};

// NOTE: portal sections are from Chargebee: https://www.chargebee.com/checkout-portal-docs/api.html#chargebee-object
export type PortalSection =
  | "ACCOUNT_DETAILS"
  | "ADDRESS"
  | "ADD_PAYMENT_SOURCE"
  | "BILLING_HISTORY"
  | "CHOOSE_PAYMENT_METHOD_FOR_SUBSCRIPTION"
  | "EDIT_ACCOUNT_DETAILS"
  | "EDIT_BILLING_ADDRESS"
  | "EDIT_PAYMENT_SOURCE"
  | "EDIT_SHIPPING_ADDRESS"
  | "EDIT_SUBSCRIPTION"
  | "EDIT_SUBSCRIPTION_CUSTOM_FIELDS"
  | "PAYMENT_SOURCES"
  | "SUBSCRIPTION_CANCELLATION"
  | "SUBSCRIPTION_DETAILS"
  | "VIEW_PAYMENT_SOURCE"
  | "VIEW_SCHEDULED_CHANGES";

export type POData = {
  rewardsPONumber?: string;
  subscriptionPONumber?: string;
};

export type PurchaseOrderNumbersCardPropTypes = {
  companyId: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type ReactivateModalProps = {
  cardType?: CreditCardTypes;
  closeModal: () => void;
  companyId: string;
  fetchData: () => void;
  last4?: string;
  modal: string;
  subscriptionType: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
  userCount: number;
};

export type CatalogShutoffModalProps = {
  catalogShutoff: boolean;
  closeModal: () => void;
  companyId: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
  fetchData: () => void;
};

export type RewardCSVDownloadIconPropTypes = {
  paymentId: string;
  testId: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type RecentInvoicesLineItemData = {
  dateInvoiceCreated: string;
  dateInvoiceDue: string;
  dateInvoicePaid: string;
  dateInvoiceVoided: string;
  description: string;
  displayAmount: string;
  displayDescription: string;
  displayError?: string;
  displayStatus: string;
  downloadIcon?: React.ReactElement;
  id: string; // invoiceId
  makePaymentButton?: React.ReactElement;
  status: string;
  statusBox: React.ReactElement;
};

export type RecentInvoicesCardPropTypes = {
  showSuperAdminFeature: boolean;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type RecentInvoicesData = {
  billingType: BillingType;
  companyId: string;
  errorMessage?: string;
  lineItems: RecentInvoicesLineItemData[];
  netTermDays: number;
  refillAmount: number;
  refillAmountDisplay: string;
  refillThresholdDisplay: string;
  rewardBalanceDisplay: string;
  rowCount: number;
  subscriptionAutoCollection: AutoCollection;
};

export type RewardBillingCardPropTypes = {
  billingType: BillingType;
  card: Card;
  fetchPaymentMethodData: (cardType) => void;
  openPortal: OpenPortal;
  paymentDueOn?: string;
  refetchData: boolean;
  refillAmount: string;
  refillThreshold: string;
  setPageLoading?: (loading: boolean) => void;
  setRefetchData: (refetchData: boolean) => void;
  setSuccessMessage: (message: string) => void;
  showSuperAdminFeature: boolean;
  subscriptionAutoCollection: AutoCollection;
  successMessage: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type RewardBillingData = {
  companyId: string;
  displayBillingType: string;
  rewardBalance: string;
  trialing: boolean;
  userCount: number;
};

export type RewardData = {
  amount: string;
  balance: string;
  date: string;
  description: string;
  id: string;
};

export type RewardBalanceLineItemData = {
  amount: string;
  balance: string;
  collapsibleDataCount: number;
  date: string;
  description: string;
  downloadIcon?: React.ReactElement;
  downloadPath: string;
  failed: boolean;
  hasCollapsibleData: boolean;
  id: string;
  invoiceId: string;
  pending: boolean;
  rewards: RewardData[];
  type: string;
  voided: boolean;
};

export type RewardBalanceData = {
  companyId: string;
  catalogStatus: string;
  errorMessage?: string;
  lineItems: RewardBalanceLineItemData[];
  rowCount: number;
  overrideDate: string;
  catalogShutoff: boolean;
};

export type RewardBalancePropTypes = {
  showSuperAdminFeature: boolean;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type SubscriptionBillingCardPropTypes = {
  billingType: BillingType;
  card: Card;
  openPortal: (
    event: React.ChangeEvent,
    sectionType?: PortalSection,
    handleClose?: () => void
  ) => void;
  paymentDueOn?: string;
  paymentMethodOnFile: boolean;
  refetchData: boolean;
  refillAmount: string;
  refillThreshold: string;
  setPageLoading: (loading: boolean) => void;
  setRefetchData: (refetchData: boolean) => void;
  showSuperAdminFeature: boolean;
  subscriptionAutoCollection: AutoCollection;
  subscriptionId: string;
  subscriptionType: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
  openReactivate: () => void;
};

export type SubscriptionData = {
  companyBlocklisted: boolean;
  companyId: string;
  costPerPeriod: string;
  costPerPeriodLabel: string;
  ctaMessage?: string;
  discountCouponName?: string;
  discountCouponValue?: number;
  discountLabel: string;
  errorMessage?: string;
  hasProratedBilling: boolean;
  isAnnual: boolean;
  isAnnualPayingMonthly: boolean;
  isMonthly: boolean;
  paymentDueLabel: string;
  paymentMethodOnFile: boolean;
  planLabel: string;
  pricingModel: string;
  seats: number;
  seatsMin: number | null;
  showDiscount: boolean;
  subscriptionBilledEvery: string;
  subscriptionCadence: number;
  subscriptionCostPerPeriod: string;
  subscriptionMonthlySeatPrice: string;
  subscriptionSavingsPerYearOnAnnual: string;
  subscriptionStatus: string;
  subscriptionTerm: string;
  subscriptionTotalCostPerMonthOnAnnual: string;
  subscriptionTotalCostPerYearOnAnnual: string;
  subscriptionTrueUpDate: string;
  trialEndDate: string;
  trialing: boolean;
  userCount: number;
  userCostPerPlanInterval: string;
  userIsCompanyAdmin: boolean;
  usersCostBreakdown: string;
  usersLabel: string;
};

export type SubscriptionCreditCardModalPropTypes = {
  cardType: CreditCardTypes;
  last4: string;
  closeModal: () => void;
  fetchData: () => void;
  modal: string;
  openPortal: OpenPortal;
  paymentMethodOnFile: boolean;
  setPageLoading: (loading: boolean) => void;
  subscriptionId: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

export type SwitchToAnnualModalBodyProps = {
  closeModal: () => void;
  fetchData: () => void;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};

type toastTypes = "error" | "info" | "success" | "warning";

export type ToastMessageOptionTypes = {
  isOpen: boolean;
  message: string;
  type: toastTypes;
};

export type ToastMessagePropTypes = {
  children: React.ReactElement;
  closeToastMessage: () => void;
  isOpen: boolean;
  messageType: toastTypes;
};

export type TrialBannerPropTypes = {
  trialEndDate: string;
};

export type UpdatePONumbersModalBodyProps = {
  closeModal: () => void;
  companyId: string;
  fetchData?: () => void;
  rewardsPONumber?: string;
  subscriptionPONumber?: string;
  updateToastMessage: (ToastMessageOptionTypes) => void;
};
